<script setup lang="ts">
    import { computed, inject, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { AccountStatus, ContextAccess, loadCurrentSubscription } from '@shared/Root/Context.access';
    import Loading from '@shared/components/Loading.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormRadioGroup from '@shared/components/Form/FormRadioGroup.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import { formatNumericText } from '@shared/utils/formatNumericText';
    import { getPrePaidBalance, PrePaidBalance } from './AccountPrePaidBalance.access';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const state = reactive<{
        isLoading: boolean;
        loadError: boolean;
        balance: PrePaidBalance | null;
        topUpAmount: number;
        topUpTarget: 'ACCOUNT' | 'SUBSCRIPTION';
    }>({
        isLoading: true,
        loadError: false,
        balance: null,
        topUpAmount: 0,
        topUpTarget: 'ACCOUNT',
    });

    (async () => {
        try {
            if (!contextAccess.state.currentSubscription) {
                contextAccess.state.currentSubscription = await loadCurrentSubscription();
            }

            state.balance = await getPrePaidBalance();
        } catch {
            state.loadError = true;
        } finally {
            state.isLoading = false;
        }
    })();

    const subscription = computed(() => contextAccess.state.currentSubscription);
    const account = computed(() => contextAccess.state.currentAccount);

    const showSubscriptionTarget = computed(() => !!subscription.value && (subscription.value.AccountCount > 1 || !!state.balance?.SubscriptionBalance));

    const isAccountActive = computed(() => account.value?.Status === AccountStatus.Active);

    const prePayTotalAmount = computed(() => (state.topUpAmount || 0) + (state.balance?.FullBalance || 0));

    const prePayUrl = computed(() => `/Account/PrePay?amount=${prePayTotalAmount.value}&target=${state.topUpTarget}&camefrom=ACCOUNT`);
</script>

<template>
    <div class="max-900">
        <h1 class="h2 m-lg-b">{{ $Resources.PrePaidHeading }}</h1>

        <Loading :show="state.isLoading" loadingStyle="progress-bar" fixed></Loading>

        <BsAlert
            v-if="state.loadError"
            :type="AlertTypes.Danger"
            :dismissible="false"
            :title="$Resources.ErrorHeading"
            :content="$Resources.ErrorContactSupport" />

        <template v-if="!state.isLoading && !state.loadError && state.balance && subscription">
            <div class="row">
                <div class="col-md-6">
                    <div class="stat-highlights panel" :class="state.topUpTarget === 'ACCOUNT' ? 'panel-primary' : 'panel-default'">
                        <div class="stat-highlights-body ug-bars">
                            <p class="bar-label">{{ $Resources.PrePaidBalanceForAccountLabel }}</p>
                            <p class="value">
                                {{ formatNumericText(state.balance.AccountBalance, { precision: 2, numberWithSpace: true }) }}
                                <small>{{ state.balance.CurrencyCode }}</small>
                            </p>
                        </div>
                    </div>
                </div>
                <div class="col-md-6" v-if="showSubscriptionTarget">
                    <div class="stat-highlights panel" :class="state.topUpTarget === 'SUBSCRIPTION' ? 'panel-primary' : 'panel-default'">
                        <div class="stat-highlights-body ug-bars">
                            <p class="bar-label">{{ $Resources.PrePaidBalanceForSubscriptionLabel }}</p>
                            <p class="value">
                                {{ formatNumericText(state.balance.SubscriptionBalance, { precision: 2, numberWithSpace: true }) }}
                                <small>{{ state.balance.CurrencyCode }}</small>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <h2 class="h3 m-0-t m-lg-b">{{ $Resources.TopUpPrePaidButton }}</h2>

            <div class="form-group" v-if="showSubscriptionTarget">
                <FormRadioGroup
                    :legend="null"
                    :options="[
                        { label: $Resources.PrepayThisAccountLabel, value: 'ACCOUNT' },
                        { label: $Resources.PrepayAllAccountsLabel, value: 'SUBSCRIPTION' },
                    ]"
                    :switchStyle="SwitchStyle.Standard"
                    :modelValue="state.topUpTarget"
                    @update:modelValue="(newVal) => (state.topUpTarget = newVal as 'ACCOUNT' | 'SUBSCRIPTION')"></FormRadioGroup>
            </div>

            <BsAlert
                v-if="!isAccountActive"
                :type="AlertTypes.Warning"
                :dismissible="false"
                :title="$Resources.CreditCardActivationRequiredHeader"
                :content="$Resources.CreditCardActivationRequiredBody"></BsAlert>

            <div class="form-group max-400" :class="{ disabled: !isAccountActive }" v-else>
                <FormInput
                    type="number"
                    min="0"
                    :label="$Resources.PrePayAmountToAddLabel"
                    :disabled="!isAccountActive"
                    :modelValue="state.topUpAmount"
                    @update:modelValue="(newVal) => (state.topUpAmount = newVal === '' ? 0 : Number(newVal))">
                    <template #button>
                        <span class="align-with-input p-xs-r p-xs-l">
                            {{ state.balance.CurrencyCode }}
                        </span>
                        <a :href="prePayUrl" class="btn btn-primary" role="button" :class="{ 'disabled pe-none': state.topUpAmount == 0 }">
                            {{ $Resources.PrePaidAddButton }}
                        </a>
                    </template>
                </FormInput>
            </div>
        </template>
    </div>
</template>
